import React from 'react'
import { useCollector, useModelValue, useForm, useModelKey } from '@morningtrain/react-resources'

export default function useFieldValue (modelName = '', defaultValue = null) {
  const collector = useCollector()
  const modelValue = useModelValue(modelName, defaultValue)

  const modelKey = useModelKey()
  const latestModelKeyRef = React.useRef(modelKey)

  const [modelNameState, setModelNameState] = React.useState(modelName)

  const [value, setValue] = React.useState(modelValue)

  const form = useForm()

  /// Handle form resets
  React.useEffect(() => {
    if (form && typeof form.onReset === 'function') {
      const disposer = form.onReset(() => {
        setValue(null)
      })

      return () => {
        if (disposer) {
          disposer()
        }
      }
    }
  }, [])

  /// Look for changes in model name
  /// When name of the field changes - we should update the collector
  React.useEffect(() => {
    if (modelNameState !== modelName) {
      setModelNameState(modelName)

      if (collector !== null) {
        collector.setValue(modelName, value)
      }
    }
  }, [modelName, modelNameState, value])

  const wasChanged = React.useRef(false)

  const updateValue = React.useCallback((nextValue, fromModel = false) => {
    const fromModelAndShouldUpdate = (fromModel === true && wasChanged.current === false) || modelKey !== latestModelKeyRef.current

    if (fromModel === false || fromModelAndShouldUpdate) {
      setValue(nextValue)
      if (fromModel === false) {
        wasChanged.current = true
        if (form && typeof form.setAsChanged === 'function') {
          if (nextValue !== modelValue) {
            form.setAsChanged()
          }
        }
      } else {
        latestModelKeyRef.current = modelKey
      }
    }

    if (collector !== null) {
      collector.setValue(modelName, nextValue)
    }
  }, [collector, modelName, modelValue, value, modelKey])

  /// When our value changes in the model - we should update our internal value
  React.useEffect(() => {
    updateValue(modelValue, true)
  }, [modelValue])

  return [value, updateValue]
}
